<script setup>
// Shared public detail-page gallery (vehicles, tours, properties, packages,
// transfers). Replaces the single-swapping-<img> pattern that was copy-pasted
// into all five Show.vue pages.
//
// Why the rewrite: the old gallery reassigned one <img>'s src on every switch,
// and those URLs are ActiveStorage *redirect* variants (302 → blob, never
// cached), with no preloading — so each switch paid a redirect round-trip +
// download before anything appeared ("slow / not working"). Here every photo
// is a stacked <img> rendered up front, so the browser fetches+decodes them
// all once; switching is just an opacity crossfade between already-decoded
// frames — instant. Blurred backdrop keeps odd-aspect phone shots looking
// intentional; contain keeps the whole vehicle visible and centred.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  // [{ full, thumb }] — right-sized WebP variants from the controller.
  photos: { type: Array, default: () => [] },
  alt:    { type: String, default: '' },
})

const { t } = useI18n()

const activeIndex = ref(0)
const hasPhotos = computed(() => props.photos.length > 0)
const multiple = computed(() => props.photos.length > 1)
const activePhoto = computed(() => props.photos[activeIndex.value] || null)

// Landscape shots fill the frame edge to edge (cover); portrait phone shots
// stay whole over the blurred backdrop (contain). Decided per photo on load,
// so a mixed set never shows blur bars on the shots that don't need them.
const fit = ref({})
function onLoad(i, e) {
  const img = e.target
  if (!img?.naturalWidth) return
  fit.value[i] = img.naturalWidth >= img.naturalHeight * 1.05 ? 'cover' : 'contain'
}

function go(i) {
  const n = props.photos.length
  if (n === 0) return
  activeIndex.value = ((i % n) + n) % n
}
const next = () => go(activeIndex.value + 1)
const prev = () => go(activeIndex.value - 1)

// Keyboard ←/→ — only while not typing in a field, and only when there's
// more than one photo. Listener is scoped to the component lifetime.
function onKey(e) {
  if (!multiple.value) return
  const tag = document.activeElement?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  if (e.key === 'ArrowRight') { e.preventDefault(); next() }
  else if (e.key === 'ArrowLeft') { e.preventDefault(); prev() }
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

// Touch swipe — horizontal drag over the main image advances the gallery.
let touchStartX = null
function onTouchStart(e) { touchStartX = e.changedTouches[0].clientX }
function onTouchEnd(e) {
  if (touchStartX === null) return
  const dx = e.changedTouches[0].clientX - touchStartX
  if (Math.abs(dx) > 40) { dx < 0 ? next() : prev() }
  touchStartX = null
}
</script>

<template>
  <div class="gallery">
    <div
      :class="['gallery__main', { 'gallery__main--wide': fit[activeIndex] === 'cover' }]"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <template v-if="hasPhotos">
        <!-- Backdrop tracks the active photo; heavy blur hides any swap flash. -->
        <div class="gallery__backdrop" :style="{ backgroundImage: `url(${activePhoto.full})` }" aria-hidden="true"></div>
        <!-- All photos stacked and preloaded; crossfade by opacity so switching
             is instant once decoded. First is high priority, rest async. -->
        <img
          v-for="(p, i) in photos"
          :key="p.thumb"
          class="gallery__photo"
          :class="{ 'is-active': i === activeIndex, 'gallery__photo--cover': fit[i] === 'cover' }"
          :src="p.full"
          @load="onLoad(i, $event)"
          :alt="i === activeIndex ? alt : ''"
          decoding="async"
          :fetchpriority="i === 0 ? 'high' : 'auto'"
          :aria-hidden="i === activeIndex ? 'false' : 'true'"
        >
        <template v-if="multiple">
          <button type="button" class="gallery__nav gallery__nav--prev" :aria-label="t('public.home.prev')" @click="prev">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button type="button" class="gallery__nav gallery__nav--next" :aria-label="t('public.home.next')" @click="next">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          <span class="gallery__count">{{ activeIndex + 1 }} / {{ photos.length }}</span>
        </template>
      </template>
      <slot v-else name="placeholder" />
    </div>
    <ul v-if="multiple" class="gallery__thumbs">
      <li v-for="(p, i) in photos" :key="p.thumb">
        <button type="button" :class="['thumb', i === activeIndex && 'is-active']" :aria-label="`${i + 1}`" @click="go(i)">
          <img :src="p.thumb" alt="" loading="lazy" decoding="async">
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.gallery__main {
  position: relative; aspect-ratio: 4 / 3; background: var(--surfaceAlt);
  border-radius: 1.25rem; overflow: hidden; display: grid; place-items: center;
  touch-action: pan-y;
}
/* Whole photo (contain — no over-zoom/crop) over a blurred, dimmed copy of
   itself, so odd-aspect phone shots fill the frame and look intentional. */
.gallery__backdrop {
  position: absolute; inset: 0; z-index: 0;
  background-size: cover; background-position: center;
  filter: blur(28px) brightness(0.82); transform: scale(1.15);
}
.gallery__photo {
  position: absolute; inset: 0; z-index: 1;
  width: 100%; height: 100%; object-fit: contain;
  opacity: 0; transition: opacity 220ms ease;
}
.gallery__photo.is-active { opacity: 1; z-index: 1; }
.gallery__photo--cover { object-fit: cover; }
/* Wider, more editorial frame on desktop — but only while the active shot is
   landscape and covers it. A portrait phone photo keeps the 4:3 frame, so the
   blurred side bands stay modest instead of growing with the frame. */
@media (min-width: 64rem) {
  .gallery__main--wide { aspect-ratio: 16 / 9; }
}
.gallery__nav {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
  width: 2.75rem; height: 2.75rem; border-radius: 999px;
  display: grid; place-items: center;
  background: color-mix(in oklch, var(--surface) 80%, transparent);
  border: 1px solid var(--line); color: var(--ink); cursor: pointer;
  backdrop-filter: blur(4px);
  box-shadow: 0 8px 22px -12px color-mix(in oklch, var(--ink) 45%, transparent);
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease, transform 150ms ease;
}
.gallery__nav svg { width: 1.3rem; height: 1.3rem; }
.gallery__nav:hover { color: var(--accent); border-color: var(--accent); transform: translateY(-50%) scale(1.06); }
.gallery__nav:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.gallery__nav--prev { left: 0.6rem; }
.gallery__nav--next { right: 0.6rem; }
.gallery__count {
  position: absolute; bottom: 0.6rem; right: 0.75rem; z-index: 2;
  font-size: 0.75rem; font-weight: 600; color: var(--surface);
  background: color-mix(in oklch, var(--ink) 55%, transparent);
  padding: 0.15rem 0.5rem; border-radius: 999px; backdrop-filter: blur(2px);
}
.gallery__thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); flex: none; }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
@media (prefers-reduced-motion: reduce) {
  .gallery__photo { transition: none; }
}
</style>
